import {
  ProjectArtifactsService,
  type ProjectArtifactDetailDto,
} from '@inference.engine/workspace-api-client/web';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import { getArtifactRowKey } from '../lib/getArtifactRowKey';
import { PROJECT_ARTIFACT_QUERY_KEYS } from './query-keys';

/**
 * Bulk-resolves render-data for the visible Library widget rows in one
 * round-trip (`POST :projectId/artifacts/detail`), so the grid can mount each
 * widget in thumbnail mode without N single-detail fetches.
 *
 * Cache unification: artifacts are immutable once attached, so the bulk and
 * single-detail endpoints return the same payload for the same row. On every
 * success we seed each returned detail into the single-detail query cache
 * under the SAME `PROJECT_ARTIFACT_QUERY_KEYS.detail` key the preview modal
 * reads — keyed by `getArtifactRowKey` (the modal opens with that row key, and
 * `useProjectArtifactDetail` uses it as the cache id). Opening a card whose
 * thumbnail was bulk-loaded is then a synchronous cache hit, zero network.
 *
 * Gated on a non-empty `ids` list so the query stays dormant on pages with no
 * widget rows.
 */
export function useBulkArtifactDetail(
  projectId: string,
  ids: string[],
): UseQueryResult<ProjectArtifactDetailDto[], Error> {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...PROJECT_ARTIFACT_QUERY_KEYS.scopedToProject(projectId), 'bulk-detail', ids],
    queryFn: async () => {
      const response = await ProjectArtifactsService.getDetails({
        client: workspaceHttp,
        path: { platformProjectId: projectId },
        body: { ids },
      });

      if (!response.data) {
        throw new Error('Failed to load bulk project artifact details');
      }

      const { details } = response.data;
      for (const detail of details) {
        queryClient.setQueryData(
          PROJECT_ARTIFACT_QUERY_KEYS.detail(projectId, getArtifactRowKey(detail)),
          detail,
        );
      }

      return details;
    },
    enabled: ids.length > 0,
    // Artifact versions are immutable — bulk detail never changes for a given
    // id set, so never refetch. Mirrors `useProjectArtifactDetail`'s policy.
    staleTime: Infinity,
  });
}
